import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../../shared/config/brand';
import { CalendarIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon, InfoIcon, CreditCardIcon, WalletIcon, CreditCardIcon as CardIcon, ExternalLinkIcon } from 'lucide-react';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
export type PaymentProvider = {
  id: string;
  name: string;
  icon: React.ReactNode;
};
interface CommonPaymentOptionsProps {
  metadata: MetadataItem[];
  redirectUrl: string;
  expiryDate: string;
  onMetadataChange: (metadata: MetadataItem[]) => void;
  onRedirectUrlChange: (url: string) => void;
  onExpiryDateChange: (date: string) => void;
  hideExpiry?: boolean;
  hideRedirect?: boolean;
  selectedPaymentProviders?: string[];
  onSelectedPaymentProvidersChange?: (providers: string[]) => void;
  storePaymentMethod?: boolean;
  onStorePaymentMethodChange?: (value: boolean) => void;
  passFeesToCustomer?: boolean;
  onPassFeesToCustomerChange?: (value: boolean) => void;
}
export function CommonPaymentOptions({
  metadata,
  redirectUrl,
  expiryDate,
  onMetadataChange,
  onRedirectUrlChange,
  onExpiryDateChange,
  hideExpiry = false,
  hideRedirect = false,
  selectedPaymentProviders = [],
  onSelectedPaymentProvidersChange,
  storePaymentMethod,
  onStorePaymentMethodChange,
  passFeesToCustomer: externalPassFees,
  onPassFeesToCustomerChange
}: CommonPaymentOptionsProps) {
  const {
    t
  } = useTranslation('payments');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [optionsSectionExpanded, setOptionsSectionExpanded] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [showPaymentProviders, setShowPaymentProviders] = useState(false);
  const [internalPassFees, setInternalPassFees] = useState(false);
  const passFeesToCustomer = externalPassFees !== undefined ? externalPassFees : internalPassFees;
  const handlePassFeesChange = (checked: boolean) => {
    if (onPassFeesToCustomerChange) {
      onPassFeesToCustomerChange(checked);
    } else {
      setInternalPassFees(checked);
    }
  };
  const paymentProviders: PaymentProvider[] = [{
    id: 'card-payments',
    name: 'Card Payments',
    icon: <CreditCardIcon size={15} className="text-[#E2231A]" data-id="element-7982" data-mp="3uklfzo687xx2" />
  }, {
    id: 'alipay',
    name: 'Alipay',
    icon: <WalletIcon size={15} className="text-[#E2231A]" data-id="element-7983" data-mp="3gk0yqwfyma5o" />
  }, {
    id: 'wechatpay',
    name: 'WechatPay',
    icon: <WalletIcon size={15} className="text-green-500" data-id="element-7984" data-mp="3llq9sc36mntr" />
  }, {
    id: 'pomelo-card',
    name: BRAND.cardName,
    icon: <CardIcon size={15} className="text-purple-500" data-id="element-7985" data-mp="37mqkr3brc6x2" />
  }, {
    id: 'linepay',
    name: 'LinePay',
    icon: <WalletIcon size={15} className="text-green-600" data-id="element-7986" data-mp="15shml38vzyiw" />
  }];
  const defaultExpiryDays = 30;
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(p => parseInt(p, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setExpiryDays(Math.ceil((expiryDateObj.getTime() - today.getTime()) / (1000 * 3600 * 24)));
    }
  }, [expiryDate]);
  useEffect(() => {
    if (!expiryDate) {
      const date = new Date();
      date.setDate(date.getDate() + defaultExpiryDays);
      onExpiryDateChange(formatDate(date));
    }
  }, [expiryDate, onExpiryDateChange, defaultExpiryDays]);
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  };
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(p => parseInt(p, 10));
    return new Date(year, month - 1, day);
  };
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = showDatePicker ? expiryDate ? parseDate(expiryDate) : today : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const weeks: (Date | null)[][] = [];
    let days: (Date | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) days.push(null);
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    calendar.month.setMonth(calendar.month.getMonth() - 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    calendar.month.setMonth(calendar.month.getMonth() + 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    onExpiryDateChange(formatDate(date));
    setShowDatePicker(false);
  };
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const togglePaymentProvider = (providerId: string) => {
    if (!onSelectedPaymentProvidersChange) return;
    const updated = selectedPaymentProviders.includes(providerId) ? selectedPaymentProviders.filter(id => id !== providerId) : [...selectedPaymentProviders, providerId];
    onSelectedPaymentProvidersChange(updated);
  };
  const hasMetadata = metadata && metadata.length > 0;
  const hasRedirectUrl = redirectUrl && redirectUrl.length > 0;
  const hasExpiryDate = expiryDate && expiryDate.length > 0;
  const hasSelectedProviders = selectedPaymentProviders && selectedPaymentProviders.length > 0;
  const hasStorePayment = storePaymentMethod === true;
  const hasPassFees = passFeesToCustomer === true;
  const hasAnyData = hasMetadata || hasRedirectUrl || hasExpiryDate || hasSelectedProviders || hasStorePayment || hasPassFees;
  const quickDates = [{
    label: '7 days',
    days: 7
  }, {
    label: '30 days',
    days: 30
  }, {
    label: '90 days',
    days: 90
  }];
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7987" data-mp="1ne738xqa9oe">
      <button className="w-full text-left flex items-center justify-between px-6 py-4" onClick={() => setOptionsSectionExpanded(!optionsSectionExpanded)} data-id="element-7988" data-mp="3trzul5nyhzer">
        <div className="flex items-center gap-2" data-id="element-7989" data-mp="bjqx9wu2e5ee">
          <SettingsIcon size={16} className="text-[#9F140F]" data-id="element-7990" data-mp="6lz46zhood8m" />
          <span className="text-sm font-semibold text-gray-900" data-id="element-7991" data-mp="1n1a9gepmhip3">
            {t('components.CommonPaymentOptions.additional_options')}
          </span>
          {!optionsSectionExpanded && hasAnyData && <span className="text-xs text-gray-500 font-normal ml-1" data-id="element-7992" data-mp="3cinvpm7jf4w4">
              —{' '}
              {[hasExpiryDate && !hideExpiry ? `Expires ${expiryDate}` : null, hasStorePayment ? 'Store card enabled' : null, hasPassFees ? 'Fees passed to customer' : null, hasSelectedProviders ? `${selectedPaymentProviders.length} methods` : null, hasRedirectUrl && !hideRedirect ? 'Redirect URL' : null, hasMetadata ? `${metadata.length} metadata` : null].filter(Boolean).join(', ')}
            </span>}
        </div>
        {optionsSectionExpanded ? <ChevronDownIcon size={16} className="text-gray-400" data-id="element-7993" data-mp="d2qwgnx9nq03" /> : <ChevronRightIcon size={16} className="text-gray-400" data-id="element-7994" data-mp="29wm0m9vdeoz8" />}
      </button>

      {optionsSectionExpanded && <div data-id="element-7995" data-mp="2ev5l05ubfsph">
          {/* ── Store Payment Method ── */}
          {onStorePaymentMethodChange !== undefined && <div className="border-t border-gray-200 px-6 py-5" data-id="element-7996" data-mp="27d7ema7fkxqh">
              <div className="flex items-start justify-between" data-id="element-7997" data-mp="22jmhekdnw7sc">
                <div data-id="element-7998" data-mp="1xkryzjjlwl8r">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-7999" data-mp="1xmctxa9u2675">
                    {t('components.CommonPaymentOptions.store_payment_method')}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8000" data-mp="2q9qmyxaeuhlz">
                    {t('components.CommonPaymentOptions.customer_authorizes_saving_their_payment_method')}{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-8001" data-mp="2gark5vcbtg6w">
                      {t('components.CommonPaymentOptions.cards')}
                    </span>{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-8002" data-mp="14xywowqkjdwn">
                      {t('components.CommonPaymentOptions.alipay')}
                    </span>{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-8003" data-mp="1k2s5n5z1f4t">
                      {t('components.CommonPaymentOptions.wechatpay')}
                    </span>
                  </p>
                </div>
                <label className="flex items-center cursor-pointer flex-shrink-0 mt-0.5 ml-4" data-id="element-8004" data-mp="aio4v1orhzxo">
                  <input type="checkbox" className="sr-only" checked={storePaymentMethod || false} onChange={e => onStorePaymentMethodChange(e.target.checked)} data-id="element-8005" data-mp="pbhu3etu5ufj" />

                  <div className={`w-10 h-[22px] rounded-full transition-colors ${storePaymentMethod ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-8006" data-mp="t95wf2xuyphg">
                    <div className={`w-[18px] h-[18px] rounded-full bg-white transform transition-transform shadow-sm ${storePaymentMethod ? 'translate-x-[18px]' : ''}`} data-id="element-8007" data-mp="2v4px6ziftzl5" />
                  </div>
                </label>
              </div>
            </div>}

          {/* ── Pass Fees to Customer ── */}
          <div className="border-t border-gray-200 px-6 py-5" data-id="element-8008" data-mp="15s9viweel7pz">
            <div className="flex items-start justify-between" data-id="element-8009" data-mp="ru38kbazss53">
              <div data-id="element-8010" data-mp="rvwdnm5yj4az">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-8011" data-mp="wswlo2mdah4q">
                  {t('components.CommonPaymentOptions.pass_fees_to_customer')}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-8012" data-mp="iuy82zfveoqa">
                  {t('components.CommonPaymentOptions.transaction_processing_fees_are_added_on')}
                </p>
              </div>
              <label className="flex items-center cursor-pointer flex-shrink-0 mt-0.5 ml-4" data-id="element-8013" data-mp="2h8hr2dvf3is5">
                <input type="checkbox" className="sr-only" checked={passFeesToCustomer} onChange={e => handlePassFeesChange(e.target.checked)} data-id="element-8014" data-mp="3q5oi7dkuj7o9" />

                <div className={`w-10 h-[22px] rounded-full transition-colors ${passFeesToCustomer ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-8015" data-mp="3h61geqo7fzuo">
                  <div className={`w-[18px] h-[18px] rounded-full bg-white transform transition-transform shadow-sm ${passFeesToCustomer ? 'translate-x-[18px]' : ''}`} data-id="element-8016" data-mp="cpql78plx1gz" />
                </div>
              </label>
            </div>
          </div>

          {/* ── Link Expiration ── */}
          {!hideExpiry && <div className="border-t border-gray-200 px-6 py-5" data-id="element-8017" data-mp="3v2ygzju4kvor">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-8018" data-mp="wgc52nmpmw6q">
                {t('components.CommonPaymentOptions.link_expiration')}
              </h4>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-8019" data-mp="yzgyqt24u46m">
                {t('components.CommonPaymentOptions.expires_in')} {expiryDays}{' '}
                {t('components.CommonPaymentOptions.days_default')}{' '}
                {defaultExpiryDays}{' '}
                {t('components.CommonPaymentOptions.days_change_in')}{' '}
                <span className="text-[#9F140F] cursor-pointer" data-id="element-8020" data-mp="1czrp7uzj5gr9">
                  {t('components.CommonPaymentOptions.payment_settings')}
                </span>
                .
              </p>
              <div className="relative mt-3" data-id="element-8021" data-mp="1mvv1hto3ivrx">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8022" data-mp="1mutgawdd8swh">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-8023" data-mp="1wmze4mqau4jl" />
                </div>
                <input type="text" className={`${inputClass} pl-9 cursor-pointer`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-8024" data-mp="1wosb1ab41aee" />

                <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-8025" data-mp="1nxiol7g16cxg">
                  {showDatePicker ? <ChevronUpIcon size={14} className="text-gray-400" data-id="element-8026" data-mp="191axdn67gb8o" /> : <ChevronDownIcon size={14} className="text-gray-400" data-id="element-8027" data-mp="15195g5q9uw6l" />}
                </div>

                {/* Date Picker — fixed width */}
                {showDatePicker && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80 left-0" data-id="element-8028" data-mp="1e048j8y0rq5o">
                    <div className="flex justify-between items-center mb-3" data-id="element-8029" data-mp="2udui2v7wr6mp">
                      <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-8030" data-mp="2pf9gard0cew5">
                        <ChevronDownIcon size={14} className="transform rotate-90" data-id="element-8031" data-mp="g97mhifx3fuv" />
                      </button>
                      <div className="text-sm font-semibold text-gray-900" data-id="element-8032" data-mp="77ra555jl3o0">
                        {calendar.month.toLocaleString('default', {
                  month: 'long',
                  year: 'numeric'
                })}
                      </div>
                      <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-8033" data-mp="l7iv5puh7kbi">
                        <ChevronDownIcon size={14} className="transform -rotate-90" data-id="element-8034" data-mp="1dumtjbz2d0eb" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1" data-id="element-8035" data-mp="1shllr95qvyu3">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} data-id="element-8036" data-mp="1iimik63r0veh">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center" data-id="element-8037" data-mp="1dk1hy5kl49pi">
                      {calendar.weeks.flatMap(week => week.map((date, i) => date ? <button key={i} type="button" disabled={isPast(date)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-colors ${isSelected(date) ? 'bg-[#E2231A] text-white' : isToday(date) ? 'border border-[#E2231A] text-[#E2231A]' : isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => handleDateSelect(date)} data-id="element-8038" data-mp="14qy621pl84wz">
                              {date.getDate()}
                            </button> : <div key={i} className="w-8 h-8" data-id="element-8039" data-mp="36ihv4xqykd37" />))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2" data-id="element-8040" data-mp="2du2kenev0gxo">
                      {quickDates.map(q => <button key={q.days} type="button" className="flex-1 text-xs font-medium text-[#9F140F] hover:bg-[#E2231A]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() + q.days);
                onExpiryDateChange(formatDate(d));
                setShowDatePicker(false);
              }} data-id="element-8041" data-mp="2isdt2utf4leb">
                          {q.label}
                        </button>)}
                    </div>
                  </div>}
              </div>
            </div>}

          {/* ── Payment Methods ── */}
          {onSelectedPaymentProvidersChange && <div className="border-t border-gray-200 px-6 py-5" data-id="element-8042" data-mp="1cc4pi6a2q7sm">
              <div className="flex items-start justify-between" data-id="element-8043" data-mp="17ebkhgkibjtx">
                <div data-id="element-8044" data-mp="17dth7zzfg5ci">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-8045" data-mp="1l5goeuuwqya2">
                    {t('components.CommonPaymentOptions.limit_payment_methods')}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-8046" data-mp="1h4octuykcham">
                    {t('components.CommonPaymentOptions.restrict_which_payment_methods_are_available')}
                  </p>
                </div>
                {selectedPaymentProviders.length > 0 && <button type="button" className="text-xs text-[#9F140F] hover:text-[#9F140F] font-medium flex-shrink-0 ml-4 mt-0.5" onClick={() => onSelectedPaymentProvidersChange([])} data-id="element-8047" data-mp="29oie3klhx76g">
                    {t('components.CommonPaymentOptions.clear')}
                  </button>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3" data-id="element-8048" data-mp="1qxsbzy3r1oen">
                {paymentProviders.map(provider => <label key={provider.id} className={`flex items-center gap-2.5 p-2.5 border rounded-lg cursor-pointer transition-colors text-sm ${selectedPaymentProviders.includes(provider.id) ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-8049" data-mp="1h27fgfhg9gca">
                    <input type="checkbox" className="h-3.5 w-3.5 text-[#E2231A] focus:ring-[#E2231A] border-gray-300 rounded" checked={selectedPaymentProviders.includes(provider.id)} onChange={() => togglePaymentProvider(provider.id)} data-id="element-8050" data-mp="1c22xel0w8djy" />

                    <span className="flex-shrink-0" data-id="element-8051" data-mp="3tfmtbtboxjyt">{provider.icon}</span>
                    <span className="text-xs font-medium text-gray-700 truncate" data-id="element-8052" data-mp="3thzai49mjqat">
                      {provider.name}
                    </span>
                  </label>)}
              </div>
              {selectedPaymentProviders.length > 0 && <p className="mt-2 text-[11px] text-gray-500 flex items-center" data-id="element-8053" data-mp="c6zvlbicqz19">
                  <InfoIcon size={11} className="mr-1 text-[#9F140F] flex-shrink-0" data-id="element-8054" data-mp="g8l1ue0dsf2h" />

                  {t('components.CommonPaymentOptions.only_selected_methods_will_be_shown')}
                </p>}
            </div>}

          {/* ── Redirect URL ── */}
          {!hideRedirect && <div className="border-t border-gray-200 px-6 py-5" data-id="element-8055" data-mp="2ofy99xb0mw7n">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-8056" data-mp="335mdb6al08z2">
                {t('components.CommonPaymentOptions.success_redirect_url')}
              </h4>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-8057" data-mp="2y7kt2i1f63m0">
                {t('components.CommonPaymentOptions.where_to_redirect_customers_after_a')}
              </p>
              <div className="relative mt-3" data-id="element-8058" data-mp="25k89mgqk2bwx">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8059" data-mp="2kfo7kwu9aryl">
                  <ExternalLinkIcon size={14} className="text-gray-400" data-id="element-8060" data-mp="2fg1rmfn1mxm3" />
                </div>
                <input type="url" className={`${inputClass} pl-9`} placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => onRedirectUrlChange(e.target.value)} data-id="element-8061" data-mp="3vts0dzp3ya22" />
              </div>
            </div>}

          {/* ── Metadata ── */}
          <div className="border-t border-gray-200 px-6 py-5" data-id="element-8062" data-mp="brgk7h36nsvf">
            <MetadataEditor metadata={metadata} onChange={onMetadataChange} data-id="element-8063" data-mp="bs85nz32ga7z" />
          </div>
        </div>}
    </div>;
}